"use client";

import React, { useMemo } from "react";

import { useTranslations } from "next-intl";

import ArrowForwardIcon from "@/icons/ui/navigation/arrow_forward.svg";
import { Step } from "@/types/form.types";
import type { StepperizeValidation } from "@/ui/AddObject/hooks/useStepperizeValidation";
import { cn } from "@/utils/cn";

interface FormStepsBreadcrumbsProps {
  steps?: Step[];
  currentStep?: number;
  onStepClick?: (stepId: string) => void;
  className?: string;
  // New stepperize integration props
  stepper?: any;
  validation?: StepperizeValidation;
}

export const FormStepsBreadcrumbs: React.FC<FormStepsBreadcrumbsProps> = ({
  steps = [],
  currentStep,
  onStepClick,
  className,
  stepper,
  validation,
}) => {
  const t = useTranslations("AddObjectForm");
  const renderSteps = useMemo(() => {
    // Use stepperize steps if available, fallback to legacy steps prop
    const stepsToRender = stepper?.steps || steps;

    if (!Array.isArray(stepsToRender) || stepsToRender.length === 0) {
      console.warn(t("FormStepsBreadcrumbs: no steps available"));
      return null;
    }

    return stepsToRender.map((step, index) => {
      // Handle both stepperize and legacy step formats
      const stepId = step.id;
      const stepLabel = step.label || step.title || stepId;
      const stepDescription = step.description;

      // Determine step status
      let isActive, isCompleted, isDisabled;

      if (stepper) {
        // Stepperize integration
        isActive = stepper.currentStep?.id === stepId;
        isCompleted =
          stepper.steps.indexOf(step) <
          stepper.steps.findIndex((s: any) => s.id === stepper.currentStep?.id);
        // In edit mode (when validation exists), don't disable future steps
        // Allow navigation to any step in edit mode
        const isEditMode = validation && !validation.blockNavigation;
        isDisabled = isEditMode ? false : !isActive && !isCompleted;
      } else {
        // Legacy mode
        isActive = step.status === "active";
        isCompleted = step.status === "completed";
        isDisabled = step.status === "disabled";
      }

      const stepClasses = cn(
        "form-step",
        isActive && "form-step-active",
        isCompleted && "form-step-completed",
        isDisabled && "form-step-disabled",
      );

      const handleClick = async () => {
        if (stepper && validation) {
          // Stepperize integration with validation
          if (validation.blockNavigation(stepId)) {
            return;
          }

          // Validate current step if moving forward
          if (validation.isForwardNavigation(stepId)) {
            const isValid = await validation.validateCurrentStep();
            if (!isValid) {
              return;
            }
          }

          // Navigate using stepperize
          stepper.switch(stepId);
        } else if (!isDisabled && onStepClick) {
          // Legacy navigation - allow navigation to non-disabled steps
          onStepClick(stepId);
        }
      };

      return (
        <div
          key={`${step.id}-${index}`}
          className="form-step-item flex items-center"
        >
          <button
            type="button"
            className={stepClasses}
            onClick={handleClick}
            disabled={isDisabled}
            aria-current={isActive ? "step" : undefined}
          >
            <div className="form-step-content">
              <span className="form-step-number">{index + 1}</span>
              {step.icon && <span className="form-step-icon">{step.icon}</span>}
              <span className="form-step-text">{stepLabel}</span>
              {stepDescription && (
                <span className="form-step-description">{stepDescription}</span>
              )}
            </div>
          </button>
          {index < steps.length - 1 && (
            <div className="form-step-separator">
              <ArrowForwardIcon className="form-step-separator-icon" />
            </div>
          )}
        </div>
      );
    });
  }, [steps, onStepClick, stepper, validation, t]);

  return (
    <div className={cn("form-steps-container", className)}>
      <div className="form-steps-wrapper">
        <nav
          className="form-steps-navigation"
          aria-label={t("Form steps")}
        >
          <div className="form-steps-list">{renderSteps}</div>
        </nav>
      </div>
    </div>
  );
};

export type { Step };
